/* eslint-disable @typescript-eslint/no-magic-numbers */
import React from 'react';
import capitalize from 'lodash/capitalize';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { Breadcrumbs } from '@cp/ds/src/components/breadcrumbs';
import { Container } from '@cp/ds/src/components/container';
import { Box } from '@cp/ds/src/components/box';
import { DeclinationMnemonic } from '@common/model/resource/declinationMnemonic';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { usePathGenerator } from '@cp/entities/clientWebRoutes';
import { Declination, useDeclination } from '@cp/shared/declination';
import { Typography } from '@mui/material';
import { useTheme } from '@cp/ds/src/hooks';
import { useCareerDeclination } from '@cp/entities/career';
import { ResumeFeaturesProvider } from '@cp/entities/resume';
import { useResumeAuthorAgeLabel } from 'src/entities/resume';
import { ResumeV2 } from 'src/widgets/resume';
import { RoutedLink } from 'src/shared/ui/link';
import {
  CATALOG_DESKTOP_COLUMN_SPACING,
  CATALOG_DESKTOP_COLUMN_WIDTH_FIXED,
  CatalogListSimilar,
  FavoriteFeature,
  ResumeCardWidgetV2,
  VerifiedFeature,
  calcMobileItemListStyleParams,
  useCatalogBreadcrumbs,
} from 'src/widgets/catalog';
import { useHelmet } from 'src/shared/helmet';
import { CatalogItemPageViewProps } from './model';
import { CONTENT_MAX_WIDTH_DESKTOP } from './const';
import { Header } from './components/header';

const View: React.FC<CatalogItemPageViewProps> = ({ pageProps: { catalogType, careerId }, id, data, isLoading }) => {
  const theme = useTheme();
  const Helmet = useHelmet();
  const isMobile = useIsMobile();
  const declination = useDeclination();
  const authorAgeLabel = useResumeAuthorAgeLabel(data);
  const generatePath = usePathGenerator();
  const careerDeclination = useCareerDeclination();

  const catalogListParams = React.useMemo(() => {
    const mobileColumnSpacing = calcMobileItemListStyleParams(theme).columnSpacing;
    const itemSpacing = isMobile ? `${mobileColumnSpacing}px` : CATALOG_DESKTOP_COLUMN_SPACING,
      itemWidth = isMobile ? `calc((100% - ${mobileColumnSpacing}px)/2)` : CATALOG_DESKTOP_COLUMN_WIDTH_FIXED;
    return { itemSpacing, itemWidth };
  }, [theme, isMobile]);

  const metaTags = React.useMemo(() => {
    if (!data) {
      return {
        title: `Анкета специалиста (#${id})`,
        description: '',
      };
    }
    return {
      title: `${capitalize(
        careerDeclination(1, careerId),
      )} ${[data.clientProfile?.name, data.clientProfile?.surname, data.clientProfile?.patronymic].filter(Boolean).join(' ')}${
        authorAgeLabel ? ` ${authorAgeLabel}` : ''
      }, ${data.city?.translation.name}${
        data.experience ? `, опыт - ${data.experience} ${declination(data.experience, DeclinationMnemonic.Year)}` : ''
      }`,
      description: `Резюме ${capitalize(careerDeclination(1, careerId, Declination.Genitive))}: Обо мне. ${data?.translation?.description}`,
    };
  }, [authorAgeLabel, careerDeclination, careerId, data, declination, id]);

  const baseBreadcrumbItems = useCatalogBreadcrumbs();
  const breadcrumbs = React.useMemo(() => {
    return [
      ...baseBreadcrumbItems,
      {
        path: generatePath({
          page: ClientWebPage.CatalogItem,
          pathParams: { id },
          pageProps: { careerId, catalogType },
        }),
        label: data ? [data.clientProfile?.name, data.clientProfile?.patronymic].join(' ') : '...',
      },
    ];
  }, [baseBreadcrumbItems, careerId, data, generatePath, id, catalogType]);

  const BreadcrumbsWrapper = isMobile ? Container : React.Fragment;

  const content = (
    <>
      <BreadcrumbsWrapper>
        <Breadcrumbs LinkComponent={RoutedLink} items={breadcrumbs} sx={{ mt: 2.5, mb: 3 }} />
      </BreadcrumbsWrapper>
      <ResumeV2 data={data} isLoading={isLoading} />
    </>
  );

  return (
    <>
      <Helmet>
        <title>{metaTags.title}</title>
        <meta content={metaTags.description} name="description" />
      </Helmet>

      <Header catalogItem={data} />

      <Box component="main" sx={{ backgroundColor: 'background.paper', pb: isMobile ? 14 : 10 }}>
        {isMobile ? content : <Container maxWidth={CONTENT_MAX_WIDTH_DESKTOP}>{content}</Container>}
        {data && (
          <Box sx={{ py: 2, mt: 2 }}>
            <Container sx={{ containerType: 'inline-size' }}>
              <Typography component="h3" sx={{ mb: 2 }} variant="h3">
                Похожие {careerDeclination(2, data.careerId, Declination.Accusative)}
              </Typography>
              <ResumeFeaturesProvider
                value={{
                  sidebar: {
                    right: [FavoriteFeature, VerifiedFeature],
                  },
                }}
              >
                <CatalogListSimilar
                  CatalogItem={ResumeCardWidgetV2}
                  request={{
                    type: data.type,
                    cityId: data.city?.id as number,
                    careerId: data.careerId,
                    pagination: 6,
                  }}
                  {...catalogListParams}
                />
              </ResumeFeaturesProvider>
            </Container>
          </Box>
        )}
      </Box>
    </>
  );
};

export default View;
